import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions, Validate } from "react-hook-form";

/**
 * Merges existing React Hook Form validators with additional validators, ensuring that all
 * validations run.
 *
 * @param existingValidate - The existing React Hook Form validator.
 * @param additionalValidates - The additional React Hook Form validators to merge.
 * @returns A new React Hook Form validator that combines the existing and additional validators.
 */
export function mergeReactHookFormValidators<
  TFieldValues extends FieldValues,
  TFieldName extends FieldPath<TFieldValues>,
  TFieldValue extends FieldPathValue<TFieldValues, TFieldName>,
>(
  existingValidate: RegisterOptions<TFieldValues, TFieldName>["validate"],
  ...additionalValidates: Array<Validate<TFieldValue, TFieldValues>>
): RegisterOptions<TFieldValues, TFieldName>["validate"] {
  const result: Validate<TFieldValue, TFieldValues> = (value, formValues) => {
    // Run the existing validators first.
    if (existingValidate != null) {
      if (typeof existingValidate === "function") {
        const result = existingValidate(value, formValues);
        if (result !== true) {
          return result;
        }
      } else if (typeof existingValidate === "object") {
        for (const validator of Object.values(existingValidate)) {
          if (typeof validator === "function") {
            const result = validator(value, formValues);
            if (result !== true) {
              return result;
            }
          }
        }
      }
    }

    // Then, run the additional validators.
    for (const additionalValidate of additionalValidates) {
      const result = additionalValidate(value, formValues);
      if (result !== true) {
        return result;
      }
    }
    return true;
  };

  return result;
}
